import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Generic registre (courrier / appels / visiteurs)
export default function RegistryModule({ kind, title, subtitle, icon: Icon, fields, columns, emptyForm }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [collabs, setCollabs] = useState([]);

  const load = () => api.get(`/registry/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [kind]);
  useEffect(() => { if (fields.some((f) => f.type === "collaborator")) api.get("/employees").then((r) => setCollabs(r.data)).catch(() => {}); }, [fields]);

  const openNew = () => { setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try { if (form.id) await api.put(`/registry/${kind}/${form.id}`, form); else await api.post(`/registry/${kind}`, form); toast.success("Enregistré"); setOpen(false); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet enregistrement ?")) return; await api.delete(`/registry/${kind}/${id}`); toast.success("Supprimé"); load(); };

  const list = items || [];
  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <Button onClick={openNew} data-testid={`add-${kind}`} className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel enregistrement</Button>
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={Icon} title="Aucun enregistrement" subtitle={subtitle} />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead>{columns.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}<TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id} className="hover:bg-slate-50" data-testid={`${kind}-row-${row.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{row.numero}</TableCell>
                  {columns.map((c) => <TableCell key={c.label} className={c.className}>{c.render(row)}</TableCell>)}
                  <TableCell className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(row)}><Pencil className="h-4 w-4 text-slate-400" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(row.id)}><Trash2 className="h-4 w-4 text-slate-300 hover:text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier" : "Nouvel"} — {title}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                  <Label className="text-xs">{f.label}{f.required && " *"}</Label>
                  {f.type === "select" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                      <SelectTrigger className="mt-1" data-testid={`field-${f.key}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "collaborator" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => {
                      const emp = collabs.find((e) => e.id === v);
                      const auto = emp && f.autofill ? f.autofill(emp) : {};
                      const merged = { ...form, [f.key]: v };
                      Object.entries(auto).forEach(([k, val]) => { if (!merged[k]) merged[k] = val; });
                      setForm(merged);
                    }}>
                      <SelectTrigger className="mt-1" data-testid={`field-${f.key}`}><SelectValue placeholder="Sélectionner un collaborateur" /></SelectTrigger>
                      <SelectContent>{collabs.map((e) => <SelectItem key={e.id} value={e.id}>{e.nom}{e.fonction ? ` — ${e.fonction}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={2} className="mt-1" data-testid={`field-${f.key}`} />
                  ) : (
                    <Input type={f.type || "text"} value={form[f.key] ?? ""} required={f.required}
                      onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                      className="mt-1" data-testid={`field-${f.key}`} />
                  )}
                </div>
              ))}
              <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" data-testid={`${kind}-submit`} className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
